// Server API for Ledgerate AI. The server keeps the conversation and decides
// what Claude is given; the browser sends only what's new and runs the tools.
import { toast } from "react-toastify";
import axiosAPI from "../config/axiosInstance";
import { store } from "../store/store";
import { setSubscriptionStatus } from "../store/userSlice";

// Same handling as the axios instance (config/axiosInstance.js) for a
// request made with fetch.
const handleAuthErrors = (status, body) => {
  if (status === 403 && body?.code === "SUBSCRIPTION_ERROR") {
    store.dispatch(setSubscriptionStatus(true));
    toast.error("Your subscription has expired. Please renew to continue accessing the service.");
  }
  if (status === 401) {
    localStorage.removeItem("token");
    if (!window.location.pathname.includes("/login")) window.location.href = "/portal/login";
  }
};

// Server-sent events from a fetch response: calls onEvent(name, data) for
// each complete event.
const readEvents = async (response, onEvent) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let end;
    while ((end = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      let name = "message";
      const data = [];
      block.split("\n").forEach((line) => {
        if (line.startsWith("event: ")) name = line.slice(7);
        else if (line.startsWith("data: ")) data.push(line.slice(6));
      });
      if (data.length) onEvent(name, JSON.parse(data.join("\n")));
    }
  }
};

// One message (or automatic fix) to Ledgerate AI:
//   A new message: { conversationId?, workflowId?, projectId?, mode, text, workflow, event? }
//   A fix request: { conversationId, mode, event: "fix", workflow }
// Resolves with { conversationId, reply: { message, steps | null, workflowSummary | null, outputs | null }, stopReason }.
// `onProgress(name, data)` gets the server's progress while Claude works:
//   ("stage", { stage: "thinking" | "writing" | "retrying" }), ("message", { text }), ("step", { step })
// Rejects with an Error carrying the server's message (and its status).
export const sendAiMessage = async (payload, { onProgress = () => {}, signal } = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch("/api/ai/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
    signal,
  });
  // Checks that fail before Claude is called answer with plain JSON.
  if (!(response.headers.get("Content-Type") || "").includes("text/event-stream")) {
    const body = await response.json().catch(() => ({}));
    handleAuthErrors(response.status, body);
    throw Object.assign(new Error(body.message || "The AI request failed. Please try again."), { status: response.status });
  }
  let result = null;
  let failure = null;
  await readEvents(response, (name, data) => {
    if (name === "done") result = data;
    else if (name === "error") failure = data;
    else onProgress(name, data);
  });
  if (failure) throw Object.assign(new Error(failure.message), { status: failure.status });
  if (!result) throw new Error("The connection to Ledgerate AI was lost. Please try again.");
  return result;
};

export const deleteAiConversation = (id) => axiosAPI("delete", `ai/conversations/${id}`);
// Plain-English rules and vocabulary for chat messages (ai/promptMask.js).
export const getAiTextRules = () => axiosAPI("get", "ai/text-rules");
// Counts a message refused on the device by this rule (the text is never sent).
export const reportBlockedMessage = (rule) => axiosAPI("post", "ai/blocked", { rule }).catch(() => {});
